/**
 * Leave — spec §5-F23, §8.1 `/leave`.
 *
 * "My leave" for everyone, plus an approval queue for anyone holding
 * `leave.approve`. Deliberately not HRMS: no balances, no accrual. Its job is
 * to make capacity maths honest.
 */

import { useState } from 'react';
import { CalendarDays, Check, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS, type LeaveType } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { CorrectionsQueue } from '@/features/attendance';
import { UserLink } from '@/components/shared/UserLink';
import { useWorkspace } from '@/features/settings/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { ApiError } from '@/lib/api';
import { useAuth, useCan } from '@/features/auth';
import { TeamLeave } from './TeamLeave';
import { useLeaveDecision, useLeaves, useRequestLeave, type LeaveView } from './api';

const TYPE_LABEL: Record<LeaveType, string> = {
  leave: 'Leave',
  half_day: 'Half day',
  wfh: 'Work from home',
};

const TYPE_HELP: Record<LeaveType, string> = {
  leave: 'Removes you from capacity for those days',
  half_day: 'Halves your capacity for that day',
  wfh: 'Does not change capacity — visibility only',
};

function statusTone(status: string): 'accent' | 'warn' | 'danger' | 'neutral' {
  if (status === 'approved') return 'accent';
  if (status === 'pending') return 'warn';
  if (status === 'rejected') return 'danger';
  return 'neutral';
}

function dateRange(leave: LeaveView): string {
  const start = new Date(leave.startDate).toLocaleDateString();
  const end = new Date(leave.endDate).toLocaleDateString();
  return start === end ? start : `${start} → ${end}`;
}

function RequestDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const request = useRequestLeave();
  const today = new Date().toISOString().slice(0, 10);
  const [type, setType] = useState<LeaveType>('leave');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle className="mb-4 text-[16px] font-semibold">Request leave</DialogTitle>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            try {
              await request.mutateAsync({ type, startDate, endDate, reason: reason || undefined });
              toast.success('Leave requested — your approver has been notified');
              onOpenChange(false);
              setReason('');
            } catch (err) {
              setError(
                err instanceof ApiError
                  ? (err.details?.[0]?.message ?? err.message)
                  : 'Could not submit the request',
              );
            }
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="leave-type">Type</Label>
            <select
              id="leave-type"
              value={type}
              onChange={(e) => setType(e.target.value as LeaveType)}
              className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
            >
              {(Object.keys(TYPE_LABEL) as LeaveType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </select>
            <p className="text-[11px] vt-muted">{TYPE_HELP[type]}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="leave-start">From</Label>
              <Input
                id="leave-start"
                data-testid="leave-start"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (e.target.value > endDate) setEndDate(e.target.value);
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="leave-end">To</Label>
              <Input
                id="leave-end"
                data-testid="leave-end"
                type="date"
                min={startDate}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="leave-reason">
              Reason <span className="font-normal vt-muted">— optional</span>
            </Label>
            <Input
              id="leave-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Helps your approver decide"
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]"
            >
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              data-testid="submit-leave"
              disabled={request.isPending}
            >
              {request.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
              Request
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** `sr_developer` → `Sr Developer`. */
function titleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Upcoming holidays — V4 F-5.
 *
 * The holiday list lived in Settings → Workspace, which only an owner or admin
 * ever opens, so the people planning around holidays could not see them. Six
 * is enough to plan a quarter without turning the leave page into a calendar.
 */
function UpcomingHolidays() {
  const { data: workspace } = useWorkspace();

  const upcoming = (workspace?.holidays ?? [])
    .map((h) => ({ ...h, at: new Date(h.date) }))
    .filter((h) => h.at.getTime() >= Date.now() - 86_400_000)
    .sort((a, b) => a.at.getTime() - b.at.getTime())
    .slice(0, 6);

  if (upcoming.length === 0) return null;

  return (
    <section data-testid="upcoming-holidays">
      <h2 className="mb-2 text-[13px] font-medium">Upcoming holidays</h2>
      <ul className="vt-surface flex flex-col overflow-hidden">
        {upcoming.map((holiday) => (
          <li
            key={`${holiday.date}-${holiday.name}`}
            className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2 text-[13px] last:border-0"
            data-testid="holiday-row"
          >
            <CalendarDays className="size-4 shrink-0 text-[var(--text-muted)]" />
            <span className="font-medium">{holiday.name}</span>
            <span className="ml-auto text-[12px] vt-muted">
              {/* The weekday is the useful part — a holiday on a Friday is a
                  long weekend, and that changes how people plan. */}
              {holiday.at.toLocaleDateString(undefined, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LeaveRow({ leave, showName }: { leave: LeaveView; showName?: boolean }) {
  const decide = useLeaveDecision();
  const can = useCan();
  const { user } = useAuth();
  const isMine = leave.userId === user?._id;
  const canApprove = can(PERMISSIONS.LEAVE_APPROVE) && !isMine && leave.status === 'pending';

  return (
    <div
      className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2.5 last:border-0"
      data-testid="leave-row"
    >
      {showName ? (
        <UserLink
          name={leave.userName}
          userId={leave.userId}
          avatarUrl={leave.userAvatarUrl ?? undefined}
          avatarOnly
          size={26}
        />
      ) : (
        <CalendarDays className="size-4 shrink-0 text-[var(--text-muted)]" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {showName && (
            <>
              <span className="text-[13px] font-medium" data-testid="leave-requester">
                <UserLink userId={leave.userId} name={leave.userName} nameOnly />
              </span>
              {leave.userDesignation && (
                <span className="text-[11px] vt-muted">{titleCase(leave.userDesignation)}</span>
              )}
            </>
          )}
          <span className="text-[13px]">{TYPE_LABEL[leave.type]}</span>
          <Badge tone={statusTone(leave.status)}>{leave.status}</Badge>
        </div>
        <div className="text-[12px] vt-muted">
          {dateRange(leave)}
          {/* The working-day count is the number an approver actually decides
              on — five calendar days over a weekend is three days of capacity. */}
          <span data-testid="leave-working-days">
            {' · '}
            {leave.workingDays} working {leave.workingDays === 1 ? 'day' : 'days'}
          </span>
          {leave.reason ? ` · ${leave.reason}` : ''}
        </div>
      </div>

      {canApprove && (
        <div className="flex shrink-0 gap-1">
          <Button
            size="sm"
            variant="subtle"
            data-testid="approve-leave"
            onClick={async () => {
              await decide.mutateAsync({ id: leave._id, action: 'approve' });
              toast.success('Leave approved — capacity updated');
            }}
          >
            <Check />
            Approve
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              await decide.mutateAsync({ id: leave._id, action: 'reject' });
              toast.message('Leave rejected');
            }}
          >
            <X />
            Reject
          </Button>
        </div>
      )}

      {isMine && leave.status === 'pending' && (
        <Button
          size="sm"
          variant="ghost"
          onClick={async () => {
            await decide.mutateAsync({ id: leave._id, action: 'cancel' });
            toast.message('Request cancelled');
          }}
        >
          Cancel
        </Button>
      )}
    </div>
  );
}

export function LeavePage() {
  const { user } = useAuth();
  const can = useCan();
  const [requestOpen, setRequestOpen] = useState(false);
  const [tab, setTab] = useState<'mine' | 'team'>('mine');
  const { data: mine = [], isLoading } = useLeaves({ userId: user?._id });
  const { data: all = [] } = useLeaves({});

  const isApprover = can(PERMISSIONS.LEAVE_APPROVE);
  const queue = all.filter((l) => l.status === 'pending' && l.userId !== user?._id);

  return (
    <>
      <PageHeader
        title="Leave"
        subtitle="Capacity maths is only honest if leave is in the system."
        toolbar={
          /**
           * V10 §6 — the Team leave tab, for `leave.approve` holders only.
           *
           * Not offered to anyone else: a tab that 403s is the dead control
           * V7-02 ruled out, and the sections behind it are scoped to what the
           * viewer may already read.
           */
          isApprover ? (
            <div className="flex gap-1" role="tablist">
              <Button
                variant={tab === 'mine' ? 'subtle' : 'ghost'}
                size="sm"
                role="tab"
                aria-selected={tab === 'mine'}
                data-testid="tab-my-leave"
                onClick={() => setTab('mine')}
              >
                My leave
              </Button>
              <Button
                variant={tab === 'team' ? 'subtle' : 'ghost'}
                size="sm"
                role="tab"
                aria-selected={tab === 'team'}
                data-testid="tab-team-leave"
                onClick={() => setTab('team')}
              >
                Team leave
              </Button>
            </div>
          ) : undefined
        }
        actions={
          // A guest holds `none` on leave (§3.1) and reaches this route only by
          // typing it; the list below is already empty for them, so the button
          // would be the one thing on the page that implied otherwise.
          can(PERMISSIONS.LEAVE_REQUEST) ? (
            <Button
              variant="primary"
              size="sm"
              data-testid="request-leave"
              onClick={() => setRequestOpen(true)}
            >
              <Plus />
              Request leave
            </Button>
          ) : undefined
        }
      />

      {isApprover && tab === 'team' ? (
        <div className="p-4">
          <TeamLeave />
        </div>
      ) : (
      <div className="flex flex-col gap-4 p-4">
        {isApprover && (
          <section>
            <h2 className="mb-2 flex items-center gap-2 text-[13px] font-medium">
              Approval queue
              {queue.length > 0 && <Badge tone="warn">{queue.length}</Badge>}
            </h2>
            <div className="vt-surface overflow-hidden" data-testid="approval-queue">
              {queue.length === 0 ? (
                <p className="px-3 py-4 text-center text-[13px] vt-muted">
                  Nothing waiting on you.
                </p>
              ) : (
                queue.map((leave) => <LeaveRow key={leave._id} leave={leave} showName />)
              )}
            </div>
          </section>
        )}

        {/* V6-04 §4 — beside the leave queue, decided by the same people with
            the same grant. An approver should not have to learn a second
            place to look. */}
        {isApprover && (
          <section data-testid="attendance-corrections-section">
            <h2 className="mb-2 text-[13px] font-medium">Attendance corrections</h2>
            <CorrectionsQueue />
          </section>
        )}

        <UpcomingHolidays />

        <section>
          <h2 className="mb-2 text-[13px] font-medium">My leave</h2>
          {isLoading ? (
            <Skeleton className="h-24" />
          ) : mine.length === 0 ? (
            <EmptyState
              icon={<CalendarDays className="size-6" />}
              title="No leave requested"
              description="Approved leave is deducted from your capacity automatically, so sprint planning and workload stay honest."
              action={
                <Button variant="primary" onClick={() => setRequestOpen(true)}>
                  <Plus />
                  Request leave
                </Button>
              }
            />
          ) : (
            <div className="vt-surface overflow-hidden" data-testid="my-leave">
              {mine.map((leave) => (
                <LeaveRow key={leave._id} leave={leave} />
              ))}
            </div>
          )}
        </section>
      </div>
      )}

      <RequestDialog open={requestOpen} onOpenChange={setRequestOpen} />
    </>
  );
}
